import { useAuthStore } from '@/store/modules/auth';
import { usePermissionsStore } from '@/store/modules/permissions';

/**
 * Vérifie si l'utilisateur a les permissions requises pour accéder à la route.
 * Retourne true si l'accès est autorisé, false sinon.
 */
export function checkRoutePermission(to) {
    const authStore = useAuthStore();
    const permissionsStore = usePermissionsStore();

    // Admin bypass
    if (authStore.isAdmin) return true;

    // Routes admin-only
    if (to.matched.some(record => record.meta.requiresAdmin)) {
        return false;
    }

    // meta.permission — permission unique
    const permission = to.meta.permission;
    if (permission && !permissionsStore.hasPermission(permission)) {
        return false;
    }

    // meta.permissions — au moins une (OR)
    const permissions = to.meta.permissions;
    if (permissions && permissions.length > 0 && !permissionsStore.hasAnyPermission(permissions)) {
        return false;
    }

    return true;
}

/**
 * Ordre de priorité des routes pour la redirection après connexion
 * ou quand l'utilisateur n'a pas accès à la route demandée.
 */
const ROUTE_PRIORITY = [
    { name: 'dashboard', permission: 'dashboard' },
    { name: 'cash-registers', permission: 'cash-registers' },
    { name: 'sales', permission: 'sales' },
    { name: 'products', permission: 'products' },
    { name: 'customers', permission: 'customers' },
    { name: 'suppliers', permission: 'suppliers' },
    { name: 'purchase-orders', permission: 'purchase-orders' },
    { name: 'supplier-invoices', permission: 'supplier-invoices' },
    { name: 'categories', permission: 'categories' },
    { name: 'inventory-overview', permission: 'inventory' },
    { name: 'inventory-history', permission: 'physical-inventories' },
    { name: 'stock-withdrawals', permission: 'stock-withdrawals' },
    { name: 'product-regularizations', permission: 'product-regularizations' },
    { name: 'notifications', permission: 'notifications' },
    { name: 'settings', permission: 'company-settings' },
];

/**
 * Trouve la première route accessible pour l'utilisateur courant.
 * Retourne un objet { name } pour le router, ou { name: 'access-denied' } si aucune route n'est accessible.
 */
export function findFirstAllowedRoute() {
    const authStore = useAuthStore();
    const permissionsStore = usePermissionsStore();

    // Admin -> toujours dashboard
    if (authStore.isAdmin) {
        return { name: 'dashboard' };
    }

    for (const route of ROUTE_PRIORITY) {
        if (permissionsStore.hasPermission(route.permission)) {
            return { name: route.name };
        }
    }

    // Aucune permission -> page profil (toujours accessible) ou accès refusé
    return { name: 'access-denied' };
}
